import React, {Component} from "react";
import PropTypes from "prop-types";
import {AsyncTypeahead} from "react-bootstrap-typeahead";
import {isObjectSchema, toArray} from "react-jsonschema-form-extras/lib/utils";
import {mapFromSchema, mapToSchema} from "react-jsonschema-form-extras/lib/TypeaheadField";
import selectn from "selectn";
import classNames from 'classnames';
import Label from "./Label";
import ErrorList from "./ErrorList";

// This component is a fork of AsyncTypeaheadField from react-jsonschema-form-extras
// https://github.com/RxNT/react-jsonschema-form-extras/blob/master/src/TypeaheadField.js
//
// The class was forked in order to support mapping this field type to an object property
// https://github.com/RxNT/react-jsonschema-form-extras/issues/99
//
// Although the changes made to the overrideOptions could be contributed back, the changes made to the JSX cannot,
// because they wouldn't work when this field type is mapped to a string property.

const DEFAULT_OPTIONS = {
  required: false,
  labelKey: "name",
  minLength: 3,
  placeholder: "Search...",
  ref: "typeahead",
};

function optionToString(fields, separator) {
  return option => {
    return fields
      .map(field => selectn(field, option))
      .filter(fieldVal => fieldVal)
      .reduce((agg, fieldVal, i) => {
        if (i === 0) {
          return fieldVal;
        } else {
          return `${agg}${separator}${fieldVal}`;
        }
      }, "");
  };
}

function mapLabelKey(labelKey) {
  if (Array.isArray(labelKey)) {
    return optionToString(labelKey, " ");
  } else if (
    typeof labelKey === "object" &&
    labelKey.fields &&
    labelKey.separator
  ) {
    let {fields, separator} = labelKey;
    return optionToString(fields, separator);
  }
  return labelKey;
}

function applyLabelKey(obj, labelKey) {
  if (typeof labelKey === "function") {
    return labelKey(obj);
  } else if (typeof labelKey === "string") {
    return obj[labelKey];
  } else {
    return obj;
  }
}

function toSelected(formData, schema, mapping, labelKey) {
  let normFormData = formData ? toArray(formData) : [];
  if (isObjectSchema(schema)) {
    return normFormData.map(selected =>
      applyLabelKey(mapFromSchema(selected, mapping), labelKey)
    );
  }
  return normFormData;
}

class BaseTypeaheadField extends Component {
  handleSelectionChange = conf => events => {
    if (events.length > 0) {
      let {mapping, cleanAfterSelection = false} = conf;
      let {schema} = this.props;
      let schemaEvents = mapToSchema(events, schema, mapping);

      this.lastSelection = schemaEvents;
      this.props.onChange(schemaEvents);
      if (cleanAfterSelection) {
        setTimeout(() => {
          if (this.refs.typeahead) {
            this.refs.typeahead.getInstance().clear();
          }
        }, 0);
      }
    }
  };

  componentDidMount() {
    let {uiSchema: {focusOnMount = false}} = this.props;
    if (focusOnMount) {
      this.refs.typeahead.getInstance().focus();
    }
  }
}

export default class AddressTypeaheadField extends BaseTypeaheadField {
  constructor(props) {
    super(props);

    this.state = {
      options: [],
    };
  }

  handleSearch = query => {
    if (!query) {
      return;
    }

    let {
      uiSchema: {
        asyncTypeahead: {
          url,
          optionsPath,
          search = (url, query) =>
            fetch(`${url}?query=${query}`).then(res => res.json()),
        },
      },
    } = this.props;

    search(url, query)
      .then(json => (optionsPath ? selectn(optionsPath, json) : json))
      .then(options => this.setState({options}));
  };

  componentDidUpdate(props) {
    if (this.props.allowNoAddress === props.allowNoAddress) return;
    if (props.allowNoAddress === true) this.refs.typeahead.getInstance().clear();
  }

  render() {
    let {
      schema,
      uiSchema: {asyncTypeahead},
      formData,
      idSchema: {$id} = {},
    } = this.props;

    let labelKey = mapLabelKey(asyncTypeahead.labelKey);
    let selected = toSelected(
      formData,
      schema,
      asyncTypeahead.mapping,
      labelKey
    );

    let typeConf = Object.assign({}, DEFAULT_OPTIONS, asyncTypeahead, {
      selected,
      labelKey,
      onChange: this.handleSelectionChange(asyncTypeahead),
      onSearch: this.handleSearch,
      options: this.state.options,
    });

    if (asyncTypeahead.overrideOptions) {

      if (typeof asyncTypeahead.overrideOptions === "function") {
        typeConf.onInputChange = userInput => {
          const newValue = asyncTypeahead.overrideOptions(userInput, this.lastSelection);
          if (newValue) {
            this.props.onChange(newValue);
          }
        };
      } else {
        typeConf.onInputChange = this.props.onChange;
      }
    }

    const fieldId = this.props.idSchema.address.$id;
    const fieldTitle = this.props.schema.properties.address.title;
    const isRequired = this.props.schema.required.indexOf('address') > -1;

    const fieldErrors = this.props.errorSchema.address && this.props.errorSchema.address.__errors;
    const wrapperClasses = classNames({'has-error': fieldErrors && fieldErrors.length});

    return (
      <div id={$id} className={wrapperClasses}>
        <Label id={fieldId} label={fieldTitle} required={isRequired}/>
        <AsyncTypeahead {...typeConf} />
        <ErrorList errors={fieldErrors}/>
      </div>
    );
  }
}

AddressTypeaheadField.propTypes = {
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.shape({
    focusOnMount: PropTypes.bool,
    asyncTypeahead: PropTypes.shape({
      url: PropTypes.string,
      optionsPath: PropTypes.string,
      mapping: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.string,
        PropTypes.object,
      ]),
      cleanAfterSelection: PropTypes.bool,
      overrideOptions: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.func,
      ]),
      search: PropTypes.func,
    }).isRequired,
  }),
};
